/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */
// libraries
import React, { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getProductCodeUploadStatus, updateProductManagerConfig } from 'state/thunks/productmanager';
// utils
// material
import { Button, Loader, Tip, displayModal } from '@insticator/insticator-ui';
import Space from 'material/space/space'
// content
// styles
import injectSheet from "react-jss";
import styles from './publishwarning_styles';
import PreviewModeCode from '../pagesetup/productgroup/previewmodecode/previewmodecode';

/* ========== ~~~~~~~~~~ MODERATION CONFIG PAGE ~~~~~~~~~~ ========== */
const PublishWarning  = injectSheet(styles)((props) => {
  const dispatch = useDispatch();
  const { tempProductManagerConfig, productManagerConfigChanges } = useSelector(state => state.productManager)
  const { siteUUID } = useSelector(state => state.session);
  const { sites } = useSelector(state => state.account) 

  const { groupIndex, action, onPublish, onCancel } = props

  const [isProcessing, setIsProcessing] = useState(false)
  const [status, setStatus] = useState(null)


  const refetchStatus = useCallback(async () => {
    console.log('fetching status')
    const response = await dispatch(getProductCodeUploadStatus({ siteId: siteUUID }))
    if(response.error) {
      setIsProcessing(false)
      setStatus('FAILED')
    } else {
      setStatus(response?.payload || 'FAILED')

      if(response.payload === 'SUCCESS' || response.payload === 'FAILED') {
        setIsProcessing(false)
        onPublish?.()
      }
  
      if(response.payload === 'IN_PROGRESS') {
        setTimeout(() => {
          refetchStatus()
        }, [7000])
      }
    }

  }, [dispatch, onPublish, siteUUID])

  const handlePublish = useCallback(async () => {
    setIsProcessing(true)
    let response;

    if(action === 'setAsCurrent' && typeof groupIndex === 'number') {
      const updatedGroups = JSON.parse(JSON.stringify(tempProductManagerConfig.groupConfigList))
      updatedGroups[groupIndex]['current'] = updatedGroups[groupIndex]['draft']
      const updatedTempProductManagerConfig = {...tempProductManagerConfig, groupConfigList: updatedGroups}
      response =  await dispatch(updateProductManagerConfig({ productManagerConfig: updatedTempProductManagerConfig, siteId: siteUUID }))
    } else {
      response = await dispatch(updateProductManagerConfig({ productManagerConfig: tempProductManagerConfig, siteId: siteUUID }))
    }

    if(response.payload && !response.payload.errorCode) {
      setTimeout(() => {
        refetchStatus()
      }, [15000])
    } else {
      setIsProcessing(false)
      setStatus('FAILED')
    }

  }, [action, dispatch, groupIndex, refetchStatus, siteUUID, tempProductManagerConfig])



  const handleCancel = () => {
    dispatch(displayModal(false))
    onCancel?.()
  }

  const saveDraftConfirm = (
    <>
      <h4>Save Draft for Testing</h4>
      <p>A draft version of your product group will be saved. These changes will <strong>NOT be visible</strong> on the live site.
      Once your draft is saved, you can preview your changes on the live site, and when satisfied, set them live.
      </p>
      <br />
      <p>Are you sure you want to save this draft?</p>
      <Space style={{marginTop: '32px'}} alignItems={'center'} justifyContent={'center'}>
        <Button text='Cancel' variant='none' color='light' handleClick={handleCancel} />
        <Button 
          text='Save Draft' 
          variant='primary' 
          disabled={isProcessing} 
          processingText={'Saving Draft'} 
          processing={isProcessing} 
          handleClick={handlePublish} 
        />
      </Space>
    </>
  )

  const publishChangeConfirm = (
    <>
      <h4>Publish Changes</h4>
      <p>You are about to publish changes. These changes will be visible on the live site.</p>
      <br />
      <p>Are you sure you want to publish these changes?</p>
      <Space style={{marginTop: '32px'}} alignItems={'center'} justifyContent={'center'}>
      <Button text='Cancel' variant='none' color='light' handleClick={handleCancel} />
      <Button 
        text='Publish Changes' 
        variant='primary' 
        disabled={isProcessing} 
        processingText={'Pulishing changes'} 
        processing={isProcessing} 
        handleClick={handlePublish} 
      />
    </Space>
    </>
  )

  const setLiveConfirm = (
    <>
      <h4>Set Live</h4>
      <p>You are about to set this draft live. The product group changes you made will become visible on the live site. </p>
      <br />
      <p>Are you sure you want to set this draft live?</p>
      <Space style={{marginTop: '32px'}} alignItems={'center'} justifyContent={'center'}>
      <Button text='Cancel' variant='none' color='light' handleClick={handleCancel} />
      <Button 
        text='Set Live' 
        variant='delete' 
        disabled={isProcessing} 
        processingText={'Setting draft to live'} 
        processing={isProcessing} 
        handleClick={handlePublish} 
      />
    </Space>
    </>
  )

  const confirmMessage = (
    <>
    {action === 'saveDraft' ? saveDraftConfirm : action === 'setAsCurrent' ? setLiveConfirm : publishChangeConfirm}
    </>
  )

  const inProgressMessage = (
    <>
      <Loader />
      <h4>Changes processing...</h4>
      <p>Changes are taking effect, please don’t refresh the page.</p>
    </>
  )

  const [isTestChangeDisable, setTestChangeDisable] = useState(true)

  const goToSite = () => window.open(`https://${sites[siteUUID]}`, '_blank')

  const saveDraftSuccessMessage = (
    <Space direction={'column'} alignItems={'center'} justifyContent={'center'}>
      <h4>Success</h4>
      <Space direction={'column'} alignItems={'flex-start'} justifyContent={'flex-start'} gap={12} style={{textAlign: 'left',}}>
        <p>Your draft was successfully saved! Preview your changes, and once satisfied, set them live.</p>
        <div>
          <strong>How to preview:</strong>
          <p>1. Copy the code below.</p>
          <p>2. Click the “Preview Changes” button.</p>
          <p>3. Navigate to an article on the site.</p>
          <p>4. Click on the address bar of your browser.</p>
          <p>5. Paste the copied code at the end of the page url.</p>
          <p>6. Refresh the page.</p>
          <p>7. “Preview Mode” is now enabled.</p>
        </div>
        <p className='errormessage'>
          You can now preview your changes on this site. These change are not visible to other users.{' '}
          <Tip 
            space={'none'} 
            icon={'question-circle'} 
            content={
              <p>
                When you’re happy with your changes, click <strong>“Set Live”</strong> from the product group. 
                This will publish those changes to the live site and be visible to other users.
              </p>
            } 
            label={''} 
          />
        </p>
      </Space>
      <PreviewModeCode handleCopy={() => setTestChangeDisable(false)} />
      <Space style={{marginTop: '32px'}} alignItems={'center'} justifyContent={'center'}>
        <Button text='Close' variant='none' color='light' handleClick={handleCancel} />
        <Button text={'Preview Changes'} variant={'primary'} disabled={isTestChangeDisable} handleClick={goToSite} />
      </Space>   
    </Space>
  )

  const publishSuccessMessage = (
    <>
      <h4>Success</h4>
      <p>Your changes have been successfully published. Your changes are now visible on the live site to all users.</p>
      <Space style={{marginTop: '32px'}} alignItems={'center'} justifyContent={'center'}>
        <Button text='Close' variant='none' color='light' handleClick={handleCancel} />
      </Space>   
    </>
  )

  const setLiveSuccessMessage = (
    <>
      <h4>Success</h4>
      <p>Your draft has been set live. Your changes are now visible on the live site to all users.</p>
      <Space style={{marginTop: '32px'}} alignItems={'center'} justifyContent={'center'}>
        <Button text='Close' variant='none' color='light' handleClick={handleCancel} />
        <Button text='Go to site' variant='primary' handleClick={goToSite} />
      </Space>   
    </>
  )

  const resultMessage = status === 'SUCCESS' ? (
    <>
      {action === 'saveDraft' ? saveDraftSuccessMessage : action === 'setAsCurrent' ? setLiveSuccessMessage : publishSuccessMessage}
    </>
  ) : (
    <>
      <h4>Failed</h4>
      <p>There was an error on publishing the changes.</p>
      <Space style={{marginTop: '32px'}} alignItems={'center'} justifyContent={'center'}>
        <Button text='Close' variant='none' color='light' handleClick={handleCancel} />
        <Button 
          text='Try again' 
          variant='primary' 
          disabled={isProcessing} 
          processing={isProcessing} 
          handleClick={handlePublish} 
        />      
      </Space>   
    </>
  )

  return (
    <div className={'publishmodal'}>
     {isProcessing ? inProgressMessage : status === null ? confirmMessage : resultMessage }
    </div>
  )
})

/* ++++++++++ --------------- EXPORTS --------------- ++++++++++ */
export default PublishWarning